import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../../i18n';
import { useTheme } from '../../context/ThemeContext';
import { askButtonPosition, readFragmentSelection, type FragmentSelection } from './fragmentSelection';

export interface FragmentAskButtonProps {
  /** Исходный текст сообщения по id — по нему проверяется, что выделено именно оно. */
  textOf: (messageId: string) => string | null | undefined;
  /** Нажали «Спросить об этом»: цитата с этой фразой встаёт над полем ввода. */
  onAsk: (messageId: string, fragment: string) => void;
}

const BUTTON = { width: 168, height: 34 };

/**
 * Сколько держим кнопку после того, как выделение пропало. На телефоне касание
 * кнопки снимает выделение РАНЬШЕ, чем приходит нажатие: без паузы кнопка
 * исчезала бы из-под пальца, и нажатие не доходило.
 */
const CLEAR_DELAY_MS = 300;

/**
 * CORE-1749 — «Спросить об этом» у выделенной фразы в сообщении Агапеллы (веб).
 *
 * Рисуется порталом в `document.body` с `position: fixed`: у react-native-web
 * каждый `View` заводит свой контекст наложения, и кнопка внутри ленты
 * оказалась бы под соседними пузырями и панелью вкладок (см. CORE-1133).
 * `mousedown` гасится — иначе нажатие на кнопку снимает выделение до клика.
 */
export const FragmentAskButton: React.FC<FragmentAskButtonProps> = ({ textOf, onAsk }) => {
  const { t } = useI18n('chats');
  const { colors: tc } = useTheme();
  const [current, setCurrent] = useState<FragmentSelection | null>(null);
  const currentRef = useRef<FragmentSelection | null>(null);
  const textOfRef = useRef(textOf);
  textOfRef.current = textOf;
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((next: FragmentSelection | null) => {
    if (next) {
      if (clearTimer.current) { clearTimeout(clearTimer.current); clearTimer.current = null; }
      currentRef.current = next;
      setCurrent(next);
      return;
    }
    if (!currentRef.current || clearTimer.current) return;
    clearTimer.current = setTimeout(() => {
      clearTimer.current = null;
      currentRef.current = null;
      setCurrent(null);
    }, CLEAR_DELAY_MS);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;
    const read = () => show(readFragmentSelection(window.getSelection() as any, (id) => textOfRef.current(id)));
    document.addEventListener('selectionchange', read);
    // Лента прокручивается, окно меняет размер — выделение уезжает, кнопка за ним.
    window.addEventListener('scroll', read, true);
    window.addEventListener('resize', read);
    return () => {
      document.removeEventListener('selectionchange', read);
      window.removeEventListener('scroll', read, true);
      window.removeEventListener('resize', read);
      if (clearTimer.current) clearTimeout(clearTimer.current);
    };
  }, [show]);

  if (!current || typeof document === 'undefined') return null;
  const { top, left } = askButtonPosition(
    current.rect, { width: window.innerWidth, height: window.innerHeight }, BUTTON,
  );
  const ask = () => {
    const picked = currentRef.current;
    if (!picked) return;
    if (clearTimer.current) { clearTimeout(clearTimer.current); clearTimer.current = null; }
    currentRef.current = null;
    setCurrent(null);
    window.getSelection()?.removeAllRanges();
    onAsk(picked.messageId, picked.text);
  };
  return ReactDOM.createPortal(
    <button
      type="button"
      data-testid="chat-fragment-ask"
      onMouseDown={(e) => e.preventDefault()}
      onClick={ask}
      style={{
        position: 'fixed', top, left, zIndex: 2147482000,
        minWidth: BUTTON.width, height: BUTTON.height, padding: '0 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        border: 'none', borderRadius: 999, cursor: 'pointer',
        background: tc.solar, color: '#FFFFFF',
        fontFamily: 'var(--ag-font-ui)', fontSize: 13, fontWeight: 600,
        boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
        whiteSpace: 'nowrap',
      }}
    >
      {t('chat.fragment.ask')}
    </button>,
    document.body,
  );
};
